import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Table, Badge, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { PUBLIC_API_BASE_URL } from '../../lib/config';

const CustomerDashboard = () => {
  const [stats, setStats] = useState({
    totalQuotes: 0,
    pendingQuotes: 0,
    confirmedQuotes: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.get(`${PUBLIC_API_BASE_URL}/customer-portal/leads`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.status === 200) {
        const leads = response.data.data.leads;
        setQuotes(leads);
        setStats({
          totalQuotes: leads.length,
          pendingQuotes: leads.filter(l => !l.customer_confirmed).length,
          confirmedQuotes: leads.filter(l => l.customer_confirmed).length
        });

        // Fetch the most recent quote for display
        if (leads.length > 0) {
          const mostRecentQuote = leads[0]; // Assuming first is most recent
          fetchQuoteDetails(mostRecentQuote.lead_id);
        }
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      if (err.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        localStorage.removeItem('customer_token');
        localStorage.removeItem('customer_data');
        navigate('/customer-portal/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchQuoteDetails = async (lead_id) => {
    try {
      const token = localStorage.getItem('customer_token');

      // Fetch quote details and fee breakdown in parallel
      const [quoteResponse, feeResponse] = await Promise.all([
        axios.get(`${PUBLIC_API_BASE_URL}/customer-portal/lead/${lead_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${PUBLIC_API_BASE_URL}/customer-portal/lead/${lead_id}/fee-breakdown`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (quoteResponse.data.status === 200) {
        setSelectedQuote(quoteResponse.data.data);
      }

      if (feeResponse.data.status === 200) {
        console.log('✅ Fee Breakdown (Dashboard):', feeResponse.data.data);
        setFeeBreakdown(feeResponse.data.data);
      }
    } catch (err) {
      console.error('Error fetching quote details:', err);
    }
  };

  const formatCurrency = (amount) => {
    return `£${Number(amount || 0).toFixed(2)}`;
  };

  const formatServiceType = (serviceType) => {
    if (!serviceType) return 'N/A';
    return serviceType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  // Extract fee data from backend response
  const legalCosts = feeBreakdown?.legalCosts || null;
  const distributionCosts = feeBreakdown?.distributionCosts || null;
  const grandTotalData = feeBreakdown?.grandTotal || null;
  const metadata = feeBreakdown?.metadata || null;

  return (
    <div>
      <h2 className="mb-4">Welcome to Your Dashboard</h2>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div
                  className="red-bg text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{ width: '60px', height: '60px' }}
                >
                  <i className="bi bi-file-earmark-text-fill" style={{ fontSize: '28px' }}></i>
                </div>
                <div>
                  <h3 className="mb-0 fw-bold">{loading ? '...' : stats.totalQuotes}</h3>
                  <p className="mb-0 text-muted">Total Quotes</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div
                  style={{
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    width: '60px',
                    height: '60px'
                  }}
                  className="text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                >
                  <i className="bi bi-clock-fill" style={{ fontSize: '28px' }}></i>
                </div>
                <div>
                  <h3 className="mb-0 fw-bold">{loading ? '...' : stats.pendingQuotes}</h3>
                  <p className="mb-0 text-muted">Quote Sent</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4}>
          <Card className="shadow-sm border-0 h-100">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div
                  style={{
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    width: '60px',
                    height: '60px'
                  }}
                  className="text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                >
                  <i className="bi bi-check-circle-fill" style={{ fontSize: '28px' }}></i>
                </div>
                <div>
                  <h3 className="mb-0 fw-bold">{loading ? '...' : stats.confirmedQuotes}</h3>
                  <p className="mb-0 text-muted">Confirmed</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* View My Quotes Section */}
      <Card className="shadow-sm border-0">
        <Card.Header className="red-bg text-white">
          <h5 className="mb-0 fw-bold">View My Quotes</h5>
        </Card.Header>
        <Card.Body className="p-5 text-center">
          <div className="mb-4">
            <i className="bi bi-file-earmark-text" style={{ fontSize: '80px', color: '#764ba2' }}></i>
          </div>
          <h4 className="mb-3">Access Your Quote Details</h4>
          <p className="text-muted mb-4">
            View detailed breakdowns of all your quotes, download PDFs, and confirm your orders.
          </p>
          <Button
            className="red-bg text-white border-0 px-4 py-2"
            size="lg"
            onClick={() => navigate('/customer-portal/my-quotes')}
          >
            View My Quotes
            <i className="bi bi-arrow-right ms-2"></i>
          </Button>
        </Card.Body>
      </Card>

      {/* Quote Breakdown Section */}
      {selectedQuote && feeBreakdown && legalCosts?.legalFee && distributionCosts?.total && grandTotalData?.gross && (
        <Card className="shadow-sm border-0 mt-4">
          <Card.Body className="p-4">
            {/* About Your Purchase Section */}
            <div className="mb-4 pb-4" style={{ borderBottom: '2px solid #f0f0f0' }}>
              <h5 className="mb-3" style={{ color: '#333', fontWeight: '600' }}>
                About Your {formatServiceType(selectedQuote.service_type)}
              </h5>
              <Table bordered className="mb-0" style={{ backgroundColor: '#fff' }}>
                <tbody>
                  {selectedQuote.customer_ids?.[0] && (
                    <tr>
                      <td style={{ width: '120px', fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Client</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                        {selectedQuote.customer_ids[0]?.forename || ''} {selectedQuote.customer_ids[0]?.surname || ''}
                      </td>
                    </tr>
                  )}
                  {selectedQuote.address && (
                    <tr>
                      <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Property</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>
                        {typeof selectedQuote.address === 'string'
                          ? selectedQuote.address
                          : [
                              selectedQuote.address.address1 || selectedQuote.address.line1,
                              selectedQuote.address.address2 || selectedQuote.address.line2,
                              selectedQuote.address.city || selectedQuote.address.town,
                              selectedQuote.address.county || selectedQuote.address.country,
                              selectedQuote.address.postcode
                            ]
                              .filter(part => part && part.trim() !== '')
                              .join(', ')
                        }
                      </td>
                    </tr>
                  )}
                  {(selectedQuote.property_value || selectedQuote.net_value) && (
                    <tr>
                      <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Price</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>{formatCurrency(selectedQuote.property_value || selectedQuote.net_value)}</td>
                    </tr>
                  )}
                  {selectedQuote.ownership_type && (
                    <tr>
                      <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Tenure</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>{selectedQuote.ownership_type}</td>
                    </tr>
                  )}
                  <tr>
                    <td style={{ fontWeight: 'bold', backgroundColor: '#f8f9fa' }}>Reference</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>{selectedQuote.lead_id}</td>
                  </tr>
                </tbody>
              </Table>
            </div>

            {/* Quote Price Section */}
            <div className="d-flex justify-content-between align-items-center mb-4 pb-4" style={{ borderBottom: '2px solid #f0f0f0' }}>
              <div>
                <h5 className="text-muted mb-2">Your Quote Price:</h5>
                <h2 className="mb-0" style={{ color: '#764ba2', fontWeight: 'bold' }}>
                  {formatCurrency(grandTotalData?.gross)}
                  <span className="text-muted" style={{ fontSize: '14px', fontWeight: 'normal' }}>
                    {' '}inc. VAT
                  </span>
                </h2>
              </div>
              <Button
                className="red-bg border-0 px-4 py-2"
                onClick={() => navigate(`/customer-portal/quote/${selectedQuote.lead_id}`)}
                style={{ fontSize: '16px', fontWeight: 'bold' }}
              >
                View Details
              </Button>
            </div>

            {/* Quote Breakdown Table */}
            <Table bordered className="mb-0" style={{ borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f8f9fa' }}>
                <tr>
                  <th style={{ width: '50%', border: '1px solid #dee2e6', padding: '6px', fontWeight: '600' }}>{formatServiceType(selectedQuote.service_type)}</th>
                  <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right', fontWeight: '600' }}>Net</th>
                  <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right', fontWeight: '600' }}>VAT</th>
                  <th style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right', fontWeight: '600' }}>Gross</th>
                </tr>
              </thead>
              <tbody>
                {/* Legal Fees Section */}
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <td colSpan="4" style={{ border: '1px solid #dee2e6', padding: '6px', fontWeight: '600' }}>Legal Costs</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Legal Fees</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.legalFee?.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.legalFee?.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.legalFee?.gross)}</td>
                </tr>

                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Telegraphic Transfer (TT) Fee</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.ttFee?.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.ttFee?.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.ttFee?.gross)}</td>
                </tr>
                <tr>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Identity Check</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.identityCheck?.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.identityCheck?.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.identityCheck?.gross)}</td>
                </tr>
                {legalCosts?.leaseholdFee?.net > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Leasehold Fee</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.leaseholdFee?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.leaseholdFee?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.leaseholdFee?.gross)}</td>
                  </tr>
                )}
                {legalCosts?.mortgageAdminFee?.net > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Mortgage Admin Fee</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.mortgageAdminFee?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.mortgageAdminFee?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.mortgageAdminFee?.gross)}</td>
                  </tr>
                )}
                {legalCosts?.helpToBuyISAFee?.net > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Help to Buy ISA Fee</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.helpToBuyISAFee?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.helpToBuyISAFee?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.helpToBuyISAFee?.gross)}</td>
                  </tr>
                )}
                {legalCosts?.helpToBuyFee?.net > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Help to Buy Fee</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.helpToBuyFee?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.helpToBuyFee?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.helpToBuyFee?.gross)}</td>
                  </tr>
                )}
                {legalCosts?.rightToBuyFee?.net > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Right to Buy Fee</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.rightToBuyFee?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.rightToBuyFee?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.rightToBuyFee?.gross)}</td>
                  </tr>
                )}
                {legalCosts?.islamicMortgageFee?.net > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Islamic Mortgage Fee</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.islamicMortgageFee?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.islamicMortgageFee?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.islamicMortgageFee?.gross)}</td>
                  </tr>
                )}
                {legalCosts?.giftedDepositFeeFirstGiftor?.net > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Gifted Deposit Fee (First Giftor)</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.giftedDepositFeeFirstGiftor?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.giftedDepositFeeFirstGiftor?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.giftedDepositFeeFirstGiftor?.gross)}</td>
                  </tr>
                )}
                {legalCosts?.giftedDepositFeeAdditionalGiftors?.net > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Gifted Deposit Fee (Additional Giftors)</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.giftedDepositFeeAdditionalGiftors?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.giftedDepositFeeAdditionalGiftors?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.giftedDepositFeeAdditionalGiftors?.gross)}</td>
                  </tr>
                )}
                {legalCosts?.buyToLetFee?.net > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Buy to Let Fee</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.buyToLetFee?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.buyToLetFee?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.buyToLetFee?.gross)}</td>
                  </tr>
                )}
                {legalCosts?.sharedOwnershipFee?.net > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Shared Ownership Fee</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.sharedOwnershipFee?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.sharedOwnershipFee?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.sharedOwnershipFee?.gross)}</td>
                  </tr>
                )}
                {legalCosts?.newBuildFee?.net > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>New Build Fee</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.newBuildFee?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.newBuildFee?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.newBuildFee?.gross)}</td>
                  </tr>
                )}
                {legalCosts?.auctionRepossessionFee?.net > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Auction / Repossession Fee</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.auctionRepossessionFee?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.auctionRepossessionFee?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.auctionRepossessionFee?.gross)}</td>
                  </tr>
                )}
                {legalCosts?.arrangingIndemnityPolicy?.net > 0 && (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Arranging an Indemnity Policy</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.arrangingIndemnityPolicy?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.arrangingIndemnityPolicy?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.arrangingIndemnityPolicy?.gross)}</td>
                  </tr>
                )}

                {/* Total Legal Cost */}
                <tr style={{ backgroundColor: '#e3f2fd', fontWeight: '700' }}>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>TOTAL LEGAL COST</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.total?.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.total?.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(legalCosts?.total?.gross)}</td>
                </tr>

                {/* Third Party Expenditure Section */}
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <td colSpan="4" style={{ border: '1px solid #dee2e6', padding: '6px', fontWeight: '600' }}><strong>Disbursement -Third Party Expenditure</strong></td>
                </tr>
                {selectedQuote?.service_type === 'Sale' ? (
                  <tr>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Official Copies</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.officialCopies?.net)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.officialCopies?.vat)}</td>
                    <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.officialCopies?.gross)}</td>
                  </tr>
                ) : (
                  <>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Stamp Duty Land Tax</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.stampDuty?.net)}</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.stampDuty?.vat)}</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.stampDuty?.gross)}</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>HM Land Registry Fee</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.hmLandRegistryFee?.net)}</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.hmLandRegistryFee?.vat)}</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.hmLandRegistryFee?.gross)}</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Land Registry Priority Search</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.landRegistrySearch?.net)}</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.landRegistrySearch?.vat)}</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.landRegistrySearch?.gross)}</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Land Charges Search</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.landChargesSearch?.net)}</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.landChargesSearch?.vat)}</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.landChargesSearch?.gross)}</td>
                    </tr>
                    <tr>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>Search Pack - Local Authority, Water & Drainage</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.searchPack?.net)}</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.searchPack?.vat)}</td>
                      <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.searchPack?.gross)}</td>
                    </tr>
                  </>
                )}

                {/* Total Distribution Cost */}
                <tr style={{ backgroundColor: '#e3f2fd', fontWeight: '700' }}>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>TOTAL DISBURSEMENT COST</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.total?.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.total?.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(distributionCosts?.total?.gross)}</td>
                </tr>

                {/* Grand Total */}
                <tr style={{ backgroundColor: '#e3f2fd', fontWeight: '700' }}>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px' }}>TOTAL</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(grandTotalData?.net)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(grandTotalData?.vat)}</td>
                  <td style={{ border: '1px solid #dee2e6', padding: '6px', textAlign: 'right' }}>{formatCurrency(grandTotalData?.gross)}</td>
                </tr>
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default CustomerDashboard;
